import { MONEY_SCALE, ZERO_MONEY, allocate, compare, isNegative, lineTotal as grossLineTotal, subtract, sum, } from '@aeris/money';
import { asHeaderDiscount, percentToStore, resolveDiscount, } from "./discount.js";
/**
 * Computes a document's totals.
 *
 * Returns a result rather than throwing, because every failure here is
 * something a person typed — a discount bigger than the sale — and a thrown
 * exception at that boundary becomes a 500 for what is a sentence the operator
 * needs to read.
 */
export const computeTotals = (lines, 
/**
 * Reais, ou o percentual que a loja pediu (F100).
 *
 * Um `Decimal` cru continua valendo e significa reais — todos os chamadores
 * anteriores passavam um, e nenhum precisou mudar. A derivação do percentual
 * para reais acontece LÁ EMBAIXO, depois do líquido, e o porquê está em
 * `resolveDiscount`.
 */
headerDiscount = ZERO_MONEY) => {
    const intent = asHeaderDiscount(headerDiscount);
    if (intent.kind === 'amount' && isNegative(intent.amount)) {
        return { ok: false, failure: { reason: 'negative-amount', index: -1 } };
    }
    // Rounded to centavos ONCE, per line, by @aeris/money. Rounding later — on
    // the total — would let a fraction of a centavo per line accumulate into a
    // document whose printed lines do not add up to its printed total.
    const gross = [];
    const net = [];
    for (const [index, line] of lines.entries()) {
        if (isNegative(line.quantity) || isNegative(line.unitPrice)) {
            return { ok: false, failure: { reason: 'negative-amount', index } };
        }
        if (isNegative(line.discountAmount)) {
            return { ok: false, failure: { reason: 'negative-amount', index } };
        }
        const lineGross = grossLineTotal(line.quantity, line.unitPrice);
        const lineNet = subtract(lineGross, line.discountAmount);
        if (isNegative(lineNet)) {
            return {
                ok: false,
                failure: { reason: 'line-discount-exceeds-line', index },
            };
        }
        gross.push(lineGross);
        net.push(lineNet);
    }
    if (lines.length === 0) {
        // `allocate` refuses an empty set, and rightly: dividing a discount across
        // nothing is not a rounding question, it is a document that does not make
        // sense. A discount on an empty document is refused rather than silently
        // dropped, because dropping it would produce a document whose header says
        // one thing and whose arithmetic says another.
        /*
         * Um percentual sobre nada é zero reais, e por isso é aceito.
         *
         * "10% de um documento vazio" não é uma contradição — é uma política
         * declarada antes de o primeiro item entrar, que é exatamente como o PDV
         * funciona: a caixa combina o desconto e depois passa a mercadoria. Já um
         * VALOR sobre nada continua recusado: R$ 20,00 de desconto num documento
         * sem itens é uma conta que não fecha, e some se for descartado em
         * silêncio.
         */
        const emptyDiscount = intent.kind === 'percent' ? true : intent.amount.units === 0n;
        return emptyDiscount
            ? {
                ok: true,
                totals: {
                    subtotal: ZERO_MONEY,
                    discount: ZERO_MONEY,
                    discountPercent: percentToStore(intent),
                    total: ZERO_MONEY,
                    lines: [],
                },
            }
            : { ok: false, failure: { reason: 'discount-without-lines' } };
    }
    const subtotal = sum(gross, MONEY_SCALE);
    const netSubtotal = sum(net, MONEY_SCALE);
    /*
     * A intenção vira reais AQUI, sobre o líquido, e não antes.
     *
     * Antes do líquido não haveria base sobre a qual aplicá-la; depois da
     * comparação seria tarde para recusá-la.
     */
    const resolved = resolveDiscount(intent, netSubtotal);
    if (!resolved.ok) {
        return resolved.failure.reason === 'percent-out-of-range'
            ? { ok: false, failure: { reason: 'percent-out-of-range' } }
            : { ok: false, failure: { reason: 'negative-amount', index: -1 } };
    }
    const headerDiscountAmount = resolved.amount;
    if (compare(headerDiscountAmount, netSubtotal) > 0) {
        return { ok: false, failure: { reason: 'discount-exceeds-total' } };
    }
    /**
     * The distribution, weighted by what each line is worth after its own
     * discount.
     *
     * Weighting by the net rather than the gross is what keeps a line that was
     * already given away from being discounted twice, and it is why no allocated
     * share can exceed the line it is taken from.
     */
    const allocated = headerDiscountAmount.units === 0n
        ? net.map(() => ZERO_MONEY)
        : allocate(headerDiscountAmount, net);
    const results = net.map((lineNet, index) => {
        const share = allocated[index] ?? ZERO_MONEY;
        return {
            gross: gross[index] ?? ZERO_MONEY,
            allocatedDiscount: share,
            lineTotal: subtract(lineNet, share),
        };
    });
    return {
        ok: true,
        totals: {
            subtotal,
            discount: headerDiscountAmount,
            discountPercent: percentToStore(intent),
            // Summed from the stored line totals rather than computed as
            // `subtotal − discounts`. Those two are equal here, and the sum is the
            // one that stays equal when somebody changes the rules above.
            total: sum(results.map((line) => line.lineTotal), MONEY_SCALE),
            lines: results,
        },
    };
};
//# sourceMappingURL=totals.js.map